import { StatusCodes } from 'http-status-codes'

import { keycloakAuth } from '@/auth/keycloak'
import { env } from '@/config/env'
import { downloadBlob } from '@/utils/file-download'

/**
 * Download a run's failure report.
 *
 * Not an `<a href>`: the endpoint is authenticated, and a plain link sends no
 * Authorization header. Not RTK Query either — this is a file to hand to the browser,
 * not state to cache, and putting a CSV in the store would keep the whole thing in
 * memory for nothing.
 *
 * **The bytes are passed through untouched, and must stay that way.** The backend marks
 * any cell a spreadsheet would otherwise execute as a formula, which is why some values
 * arrive with a leading apostrophe; that marking is what makes the file safe to open in
 * Excel, and the backend removes it again when the file is sent back. Parsing or
 * rewriting the CSV here would undo it.
 *
 * Because it sidesteps the data-fetching layer it also sidesteps that layer's 401
 * recovery, so it repeats it: one forced token refresh and one retry, matching
 * `createReauthBaseQuery`. This is the request most likely to need it — the run has
 * finished, polling has stopped, and the tab may have sat idle past the token's life
 * before the admin comes back for the file (KPS-377).
 */
async function requestReport(runId: number): Promise<Response> {
  const token = localStorage.getItem('authToken')
  return fetch(
    `${env.apiBaseUrl}/public/v1/bulk-imports/${runId}/failures.csv`,
    {
      headers: token ? { authorization: `Bearer ${token}` } : undefined,
    },
  )
}

export async function downloadFailureReport(runId: number): Promise<void> {
  let response = await requestReport(runId)

  if (response.status === StatusCodes.UNAUTHORIZED) {
    // Best-effort, as in the base query: a refresh that fails surfaces the original 401
    // rather than a thrown exception from the refresh itself.
    const refreshed = await keycloakAuth.forceTokenRefresh().catch(() => false)
    if (refreshed) response = await requestReport(runId)
  }

  if (!response.ok) {
    throw new Error(`Failure report request failed: ${response.status}`)
  }

  downloadBlob(await response.blob(), `bulk-import-${runId}-failures.csv`)
}
